"use client";

// Color hex map mirrors the FilterPanel swatches plus a few extras for
// per-variety options that the catalog filter does not expose.
const COLOR_HEX: Record<string, string> = {
  red: "#DC143C",
  pink: "#FFB6C1",
  "hot-pink": "#FF69B4",
  peach: "#FFA07A",
  white: "#FAFAFA",
  cream: "#FFF8DC",
  blue: "#4169E1",
  green: "#9CAF88",
  purple: "#9370DB",
  orange: "#FFA500",
  yellow: "#FFD700",
  bicolor: "#FFD700",
  lavender: "#E6E6FA",
  "light-pink": "#FFD1DC",
  lilac: "#C8A2C8",
  brown: "#8B4513",
  sand: "#D2B48C",
  burgundy: "#800020",
  apricot: "#FBCEB1",
  picotee: "#F8F0E3",
};

function colorLabel(color: string): string {
  return color
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export type ColorChoice = string;
export const MIX_VALUE: ColorChoice = "mix";

export function ColorPicker({
  options,
  active,
  allowMix,
  onChange,
  variant = "card",
  testIdPrefix = "color",
}: {
  options: string[];
  active: ColorChoice;
  allowMix: boolean;
  onChange: (c: ColorChoice) => void;
  variant?: "card" | "detail";
  testIdPrefix?: string;
}) {
  // 24px minimum tap target (WCAG 2.2 / Lighthouse target-size).
  const sizeClass = "h-6 w-6";
  return (
    <div
      data-testid={`${testIdPrefix}-picker`}
      className="flex flex-wrap items-center gap-1.5"
    >
      {options.map((c) => {
        const isActive = c === active;
        const bg =
          c === "bicolor"
            ? "linear-gradient(135deg, #FFD700 50%, #DC143C 50%)"
            : COLOR_HEX[c] ?? "#CCCCCC";
        return (
          <button
            key={c}
            type="button"
            data-testid={`${testIdPrefix}-${c}`}
            aria-label={colorLabel(c)}
            aria-pressed={isActive}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(c);
            }}
            className={
              isActive
                ? `${sizeClass} rounded-full border-2 border-brand-teal ring-2 ring-brand-teal/30`
                : `${sizeClass} rounded-full border border-brand-border hover:border-brand-text-muted`
            }
            style={
              c === "bicolor"
                ? { background: bg }
                : { backgroundColor: bg }
            }
          />
        );
      })}
      {allowMix && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-mix`}
          aria-pressed={active === MIX_VALUE}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange(MIX_VALUE);
          }}
          className={
            active === MIX_VALUE
              ? "min-h-6 rounded-full bg-brand-teal px-2.5 py-0.5 text-[11px] font-semibold text-white"
              : "min-h-6 rounded-full border border-brand-border px-2.5 py-0.5 text-[11px] font-medium text-brand-text-muted hover:border-brand-teal hover:text-brand-text"
          }
        >
          Mix
        </button>
      )}
    </div>
  );
}

export function colorChoiceLabel(choice: ColorChoice): string {
  if (choice === MIX_VALUE) return "Assorted - farm's seasonal pick";
  return colorLabel(choice);
}
